import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, Chip,
  List, ListItem, ListItemIcon, ListItemText, CircularProgress, IconButton, Divider,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

const CHECKOUT_FN = 'https://us-central1-football-for-nerds.cloudfunctions.net/createCheckoutSession';
const PAYMENT_LINK = 'https://buy.stripe.com/7sYaEQ7mb4z46bB46O6wE00';

const PREMIUM_FEATURES = [
  { icon: '📊', text: 'EPA per play — every QB, RB, WR, TE and K' },
  { icon: '🎯', text: 'QB Situational Splits — 15 categories: shotgun, pressure, down, quarter, game script' },
  { icon: '🗺️', text: 'Drive-Level EPA Explorer — filter by field position zone for all 32 teams' },
  { icon: '🔴', text: 'Red Zone Tendencies — pass%, run%, TD%, goal-to-go splits for all 32 teams' },
  { icon: '🧠', text: 'Advanced team metrics — success rate, explosive rate, CPOE, pressure rate' },
  { icon: '🏈', text: 'Player deep dives — aDOT, YAC, WOPR, RACR, dakota composite QB score' },
];

const COMING_SOON = [
  { icon: '🔍', text: 'Situational Play Explorer — filter by down, distance, formation & see EPA + conversion %' },
  { icon: '⚖️', text: 'Player Comparison Tool — side-by-side EPA, success rate, air yards, deep ball %' },
  { icon: '📐', text: 'Team Style Profiles — auto-classify every team by playcalling identity' },
  { icon: '📈', text: 'Win Probability Model — swing-play graphs for any game' },
  { icon: '🏆', text: 'Fantasy projections — NR × snap share × target share' },
];

export default function UpgradeModal() {
  const { currentUser, upgradeModalOpen, setUpgradeModalOpen, setAuthModalOpen } = useAuth();
  const [loading, setLoading] = useState(false);

  const close = () => setUpgradeModalOpen(false);

  const handleUpgrade = async () => {
    if (!currentUser) {
      close();
      setAuthModalOpen(true);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(CHECKOUT_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: currentUser.uid, email: currentUser.email }),
      });
      if (resp.ok) {
        const { url } = await resp.json();
        if (url) { window.location.href = url; return; }
      }
    } catch (_) {}
    window.location.href = PAYMENT_LINK;
  };

  return (
    <Dialog open={upgradeModalOpen} onClose={close} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(8,18,32,0.99)',
          border: '1px solid #f5a62344',
          borderRadius: 3,
          backgroundImage: 'none',
          boxShadow: '0 0 80px rgba(245,166,35,0.15)',
        },
      }}>
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{
          p: 3, pb: 2,
          background: 'linear-gradient(135deg, rgba(14,28,48,0.8) 0%, rgba(20,14,6,0.8) 100%)',
          borderBottom: '1px solid #f5a62322',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>⚡</Typography>
              <Box>
                <Chip label="NERDS+" size="small" sx={{
                  fontWeight: 900, fontSize: '0.7rem', letterSpacing: '0.08em',
                  bgcolor: '#2a1a05', color: '#f5a623', border: '1px solid #f5a62366', mb: 0.5,
                }} />
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>$6</Typography>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>/month · cancel anytime</Typography>
                </Box>
              </Box>
            </Box>
            <Typography sx={{ color: '#9eb8d0', fontSize: '0.88rem' }}>
              The analytics that win fantasy leagues and bar arguments.
            </Typography>
          </Box>
          <IconButton onClick={close} size="small" sx={{ color: '#4a6080', mt: -0.5 }}>✕</IconButton>
        </Box>

        {/* Features */}
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#f5a623', mb: 1.5, letterSpacing: '0.08em' }}>
            WHAT YOU UNLOCK:
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
            {PREMIUM_FEATURES.map(f => (
              <Box key={f.text} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.4, flexShrink: 0 }}>{f.icon}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#c0d4e8', lineHeight: 1.4 }}>{f.text}</Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ borderColor: '#1a3050', mb: 2 }} />

          {/* Coming soon */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#9eb8d0', mb: 1, letterSpacing: '0.06em' }}>
            🔬 ON THE ROADMAP FOR NERDS+:
          </Typography>
          <List dense disablePadding sx={{ mb: 2.5 }}>
            {COMING_SOON.map(f => (
              <ListItem key={f.text} disableGutters sx={{ py: 0.2 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Box sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{f.icon}</Box>
                </ListItemIcon>
                <ListItemText primary={f.text} primaryTypographyProps={{ sx: { fontSize: '0.77rem', color: '#6a8aa8' } }} />
              </ListItem>
            ))}
          </List>

          {/* CTA */}
          <Button fullWidth variant="contained" color="secondary" size="large" onClick={handleUpgrade}
            disabled={loading}
            sx={{
              textTransform: 'none', fontWeight: 800, py: 1.4, fontSize: '1rem',
              boxShadow: '0 4px 24px rgba(245,166,35,0.3)',
              '&:hover': { boxShadow: '0 6px 32px rgba(245,166,35,0.45)' },
            }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : '⚡ Upgrade to Nerds+ — $6/mo'}
          </Button>
          <Typography sx={{ fontSize: '0.7rem', color: '#3a5070', textAlign: 'center', mt: 1 }}>
            Powered by Stripe · Secure checkout · Cancel anytime from your account
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
